import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Crown, ShieldCheck, Zap } from 'lucide-react';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePublicSeo } from '@/utils/seo';

const FREE_FEATURES = [
  'Accès à la plateforme vendeur',
  'Création de livraisons standard',
  'Suivi de colis',
  'Statistiques de base',
];

const PRO_FEATURES = [
  'Service livraison express',
  'Assurance colis',
  'Intégrations Shopify / WooCommerce',
  'Portefeuille avancé',
  'Programme d’affiliation',
  'Badges vendeur premium',
  'Priorité de traitement des livraisons',
];

const PricingPage = () => {
  usePublicSeo({
    title: 'Tarifs Coli Noki | Free vs Coli Noki Pro',
    description:
      'Comparez les plans Gratuit et Coli Noki Pro : livraison express, assurance colis, intégrations, portefeuille avancé et affiliation.',
    path: '/pricing',
  });

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-1">
        <section className="bg-gradient-to-br from-red-50 via-white to-cyan-50 py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-10">
              <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-4">Tarifs Coli Noki</h1>
              <p className="text-gray-700 max-w-3xl mx-auto">
                Choisissez un plan adapté à votre activité. La plateforme Coli Noki reste la source de vérité des droits et options actives.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-2 border-gray-200">
                <CardHeader>
                  <Badge className="w-fit bg-gray-200 text-gray-800 hover:bg-gray-200">GRATUIT</Badge>
                  <CardTitle className="text-2xl">Plan Gratuit</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {FREE_FEATURES.map((item) => (
                    <div key={item} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600" />
                      <span>{item}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="border-2 border-red-200 shadow-md">
                <CardHeader>
                  <Badge className="w-fit bg-red-600 text-white hover:bg-red-600">COLI NOKI PRO</Badge>
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <Crown className="w-5 h-5 text-amber-500" />
                    Plan Pro
                  </CardTitle>
                  <p className="text-sm font-medium text-red-700">À partir de 2000 FCFA / mois</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {PRO_FEATURES.map((item) => (
                    <div key={item} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-red-600" />
                      <span>{item}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-500" />
                    Service livraison express
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-700 space-y-2">
                  <p>Supplément express : <strong>+75%</strong> sur le transport.</p>
                  <p>Le service express reste une option de livraison, réservée aux vendeurs Pro actifs.</p>
                  <p>Traitement prioritaire des livraisons selon disponibilité opérationnelle.</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-cyan-600" />
                    Assurance colis
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-700 space-y-2">
                  <p>Supplément assurance : <strong>10% à 15%</strong> de la valeur du colis.</p>
                  <p>Le calcul détaillé est renvoyé par le système pendant l&apos;estimation et la création.</p>
                  <p>L’option assurance est réservée aux vendeurs Pro actifs.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="pb-16">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Commencez avec Coli Noki</h2>
            <p className="text-gray-700 mb-6">
              Créez un compte vendeur, activez Coli Noki Pro quand vous avez besoin des options premium, et gardez la flexibilité du plan Gratuit.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button asChild className="bg-red-600 hover:bg-red-700">
                <Link to="/register">Créer un compte</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/faq">Consulter la FAQ</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <FooterNew />
    </div>
  );
};

export default PricingPage;
